import { useState, type FormEvent } from 'react'
import { useQuery } from '@tanstack/react-query'
import { Compass, Search } from 'lucide-react'
import { discoveryApi, type Category, type DiscoveryCard } from '@/lib/discoveryApi'
import { describeError } from '@/lib/auth'
import { guildApi } from '@/lib/guildApi'
import { useAppStore } from '@/store/app'
import { useGuildStore } from '@/store/guilds'
import { Avatar } from '@/components/Avatar'
import { guildIconUrl } from '@/lib/avatar'
import { Modal } from '@/components/settings/Modal'
import { MobileBack } from '@/components/MobileBack'
import { DiscoveryCard as ServerCard } from './DiscoveryCard'
import { DiscoveryBanner } from './DiscoveryBanner'
import { plural } from '@/lib/guildUi'
import styles from './DiscoveryPage.module.css'

/** Каталог публичных серверов: поиск, категории, карточки и предпросмотр перед вступлением. */
export function DiscoveryPage() {
  const [category, setCategory] = useState('')
  const [query, setQuery] = useState('')
  const [search, setSearch] = useState('')
  const [selected, setSelected] = useState<DiscoveryCard | null>(null)
  const guilds = useGuildStore((s) => s.guilds)
  const setView = useAppStore((s) => s.setView)
  const showNav = useAppStore((s) => s.showNav)

  const categoriesQuery = useQuery({ queryKey: ['discovery-categories'], queryFn: () => discoveryApi.categories(), staleTime: 3_600_000 })
  const listQuery = useQuery({ queryKey: ['discovery', search, category], queryFn: () => discoveryApi.list({ q: search, category }) })
  const categories: Category[] = categoriesQuery.data?.categories ?? []
  const cards = listQuery.data?.guilds ?? []
  const loading = listQuery.isPending
  const error = listQuery.error ? describeError(listQuery.error).message : null

  const submit = (e: FormEvent) => {
    e.preventDefault()
    setSearch(query.trim())
  }

  const categoryName = (id: string) => categories.find((c) => c.id === id)?.name ?? id

  return (
    <section className={styles.page} aria-label="Каталог серверов">
      <header className={styles.hero}>
        <h1>
          {/* на телефоне каталог занимает весь экран и рейки серверов не видно: без стрелки отсюда не выйти */}
          <MobileBack label="К личным сообщениям" onBack={() => { setView({ kind: 'home', page: 'friends' }); showNav() }} />
          <Compass size={22} /> Каталог серверов
        </h1>
        <p>Открытые сообщества Кличата. Посмотрите карточку — сколько людей онлайн и о чём сервер — и вступайте в один клик.</p>
        <form className={styles.search} onSubmit={submit}>
          <Search size={16} />
          <input value={query} onChange={(e) => setQuery(e.target.value)} placeholder="Название, тема или тег" aria-label="Поиск по каталогу" />
          <button type="submit">Найти</button>
        </form>
      </header>

      <div className={styles.categories} role="tablist" aria-label="Категории">
        <button type="button" role="tab" aria-selected={category === ''} className={styles.chip} onClick={() => setCategory('')}>Все</button>
        {categories.map((c) => (
          <button key={c.id} type="button" role="tab" aria-selected={category === c.id} className={styles.chip} onClick={() => setCategory(c.id)}>{c.name}</button>
        ))}
      </div>

      {error ? <p className={styles.error} role="alert">{error}</p> : null}
      {loading ? <p className={styles.empty}>Загружаем…</p> : null}
      {!loading && cards.length === 0 && !error ? (
        <p className={styles.empty}>
          {search || category ? 'Ничего не нашлось — попробуйте другой запрос.' : 'В каталоге пока пусто. Свой сервер можно опубликовать в его настройках, вкладка «Каталог».'}
        </p>
      ) : null}

      <div className={styles.grid}>
        {cards.map(c => <ServerCard key={c.guild_id} card={c} onOpen={() => setSelected(c)} />)}
      </div>

      {selected ? <PreviewModal card={selected} categoryName={categoryName(selected.category)} joined={Boolean(guilds[selected.guild_id])} onClose={() => setSelected(null)} /> : null}
    </section>
  )
}

function PreviewModal({ card, categoryName, joined, onClose }: { card: DiscoveryCard; categoryName: string; joined: boolean; onClose: () => void }) {
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const setView = useAppStore((s) => s.setView)
  const applyEvent = useGuildStore((s) => s.applyEvent)

  const open = () => {
    setView({ kind: 'guild', guildId: card.guild_id, channelId: null })
    onClose()
  }

  const join = async () => {
    setBusy(true)
    setError(null)
    try {
      const guild = await guildApi.joinDiscovery(card.guild_id)
      applyEvent('GUILD_CREATE', guild)
      open()
    } catch (err) {
      setError(describeError(err).message)
    } finally {
      setBusy(false)
    }
  }

  return (
    <Modal title={card.name} onClose={onClose} width={480}>
      {card.banner ? <DiscoveryBanner key={card.banner} guildId={card.guild_id} hash={card.banner} className={styles.previewBanner} /> : null}
      <div className={styles.preview}>
        <Avatar initial={card.name.slice(0, 1).toUpperCase()} color="#5865F2" size={64} src={guildIconUrl(card.guild_id, card.icon)} />
        <div>
          <p className={styles.previewDesc}>{card.description}</p>
          <p className={styles.previewMeta}>
            {card.approximate_presence_count.toLocaleString('ru-RU')} онлайн · {card.approximate_member_count.toLocaleString('ru-RU')} {plural(card.approximate_member_count, 'участник', 'участника', 'участников')} · {categoryName}
          </p>
          {card.tags.length > 0 ? <div className={styles.tags}>{card.tags.map((t) => <span key={t}>#{t}</span>)}</div> : null}
        </div>
      </div>
      {error ? <p className={styles.error} role="alert">{error}</p> : null}
      <div className={styles.previewActions}>
        {joined ? (
          <button type="button" className={styles.join} onClick={open}>Открыть сервер</button>
        ) : (
          <button type="button" className={styles.join} disabled={busy} onClick={() => void join()}>{busy ? 'Вступаем…' : 'Вступить'}</button>
        )}
      </div>
    </Modal>
  )
}
